// Formulářová pole z kontraktu FieldDef: vykreslení, hodnota, chyby, podmínky viditelnosti (stejná pravidla jako server).
import { h } from '../dom';
import { t, tk, toInputDate, toInputDateTime, fmtDate, fmtDateTime } from '../i18n';
import { icon } from '../icons';
import type { Condition, FieldDef } from '../contract';
import { makeComplex, type ValueControl } from './fields-complex';
import { aiEnabled, aiFieldButton } from './ai';

export function evalCondition(cond: Condition, data: Record<string, unknown>): boolean {
  const op = cond[0];
  if (op === 'and' || op === 'or') {
    const res = cond.slice(1).map((c) => Array.isArray(c) && evalCondition(c, data));
    return op === 'and' ? !res.includes(false) : res.includes(true);
  }
  if (op === 'not') return !(Array.isArray(cond[1]) && evalCondition(cond[1], data));
  if (typeof op !== 'string') return false;
  const value = data[op];
  const cmp = cond[1] ?? '=';
  const expected = cond[2];
  switch (cmp) {
    case '=':
      return String(value ?? '') === String(expected ?? '');
    case '!=':
      return String(value ?? '') !== String(expected ?? '');
    case 'in':
      return Array.isArray(expected) && expected.some((x) => String(x) === String(value ?? ''));
    case 'truthy':
      return Boolean(value) && value !== '0';
    default:
      return false;
  }
}

export class FieldControl {
  readonly el: HTMLElement;
  private input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
  /** co se v režimu úprav ukazuje místo hodnoty: samotné pole, nebo pole s posuvníkem */
  private control: HTMLElement;
  private errorEl = h('div', { class: 'field-error' });
  private viewEl = h('div', { class: 'field-view' });
  private complex: ValueControl | null;
  onChange: (() => void) | null = null;

  constructor(readonly def: FieldDef, readonly layout: 'stack' | 'rows' = 'stack') {
    this.complex = makeComplex(def);
    if (this.complex) this.complex.onChange = () => this.onChange?.();
    this.input = this.makeInput();
    if (def.placeholder && 'placeholder' in this.input) this.input.placeholder = def.placeholder;
    if (layout === 'rows') {
      this.input.addEventListener('input', () => this.onChange?.());
      this.input.addEventListener('change', () => this.onChange?.());
      this.control = this.input;
      this.el = this.buildRow();
      return;
    }
    this.control = def.slider ? this.makeSlider(this.input as HTMLInputElement) : def.unit ? h('div', { class: 'field-unit-wrap' }, this.input, h('span', { class: 'field-unit' }, tk(def.unit))) : this.input;
    this.input.addEventListener('input', () => this.onChange?.());
    this.input.addEventListener('change', () => this.onChange?.());
    // úzké pole: nápověda jako ⓘ u popisku (bublina), ať pole nerozšiřuje
    const tip = def.help && def.width === 'auto' ? h('span', { class: 'field-tip', title: tk(def.help), tabindex: '0', 'aria-label': tk(def.help) }, 'ⓘ') : null;
    const label = h('label', { class: 'field-label', for: this.inputId() }, tk(def.label), def.required ? h('span', { class: 'req' }, ' *') : null, tip);
    // pomoc s textem (kap. 34): tlačítko AI u textových polí, jen když má uživatel AI zapnutou a pole není jen pro čtení
    const aiBtn = (def.type === 'textarea' || (def.type === 'text' && (def.max ?? 0) > 500)) && !def.readonly && def.ai !== false && aiEnabled()
      ? aiFieldButton(() => this.input.value, (v) => { this.input.value = v; this.renderView(v); this.onChange?.(); }, () => !this.input.hidden)
      : null;
    const labelRow = aiBtn ? h('div', { class: 'field-label-row' }, label, aiBtn) : label;
    this.el = h(
      'div',
      { class: 'field w-' + def.width + (def.type === 'bool' ? ' field-bool' : '') + (this.complex ? ' field-complex' : ''), dataset: { key: def.key } },
      def.type === 'bool' ? h('label', { class: 'check' }, this.input, this.boolMark, h('span', null, tk(def.label))) : this.complex ? [label, this.complex.el] : [labelRow, this.control, this.viewEl],
      def.help && def.width !== 'auto' ? h('div', { class: 'field-help' }, tk(def.help)) : null,
      this.errorEl,
    );
  }

  /**
   * Řádek nastavení (návrh vzhledu 3. 11.): popisek + popis | doplněk (posuvník / původ hodnoty) | hodnota | jednotka –
   * stálé sloupce, takže pole jsou na celé stránce pod sebou bez ohledu na délku popisku. Široké prvky (text, štítky,
   * řádky) mají popisek nahoře a prvek přes celou šířku.
   */
  private buildRow(): HTMLElement {
    const d = this.def;
    const help = d.help ? tk(d.help) : '';
    const longHelp = help.length > 90;
    const tip = longHelp ? h('span', { class: 'frow-tip', title: help, tabindex: '0', 'aria-label': help }, icon('info')) : null;
    const label = h('label', { class: 'frow-name', for: this.inputId() }, d.follows ? h('span', { class: 'frow-conn' }, tk(d.follows)) : null, tk(d.label), d.required ? h('span', { class: 'req' }, ' *') : null, tip);
    const labelCol = h('div', { class: 'frow-label' }, label, help && !longHelp ? h('div', { class: 'frow-desc' }, help) : null);
    const wide = this.complex !== null && d.type !== 'select' || d.type === 'textarea';
    const cls = 'field frow' + (wide ? ' frow-wide' : '') + (d.type === 'bool' ? ' frow-bool' : '') + (d.type === 'select' ? ' frow-select' : '') + (d.follows ? ' frow-follows' : '') + (d.type === 'number' ? ' frow-num' : '');
    if (wide) {
      return h('div', { class: cls, dataset: { key: d.key } }, labelCol, this.complex ? this.complex.el : [this.input, this.viewEl], this.errorEl);
    }
    let aux: Node | null = null;
    if (d.slider && d.type === 'number') {
      const min = d.min ?? 0;
      const max = d.maxValue ?? 100;
      const range = h('input', { type: 'range', class: 'field-range', min: String(min), max: String(max), step: Number.isInteger(min) && Number.isInteger(max) ? '1' : 'any', 'aria-label': tk(d.label) }) as HTMLInputElement;
      const num = this.input as HTMLInputElement;
      const sync = (): void => { const v = num.value === '' ? Number(d.default ?? min) : Number(num.value); range.value = String(Number.isNaN(v) ? min : v); };
      range.addEventListener('input', () => { num.value = range.value; this.renderView(Number(range.value)); this.onChange?.(); });
      num.addEventListener('input', sync);
      this.syncSlider = sync;
      this.rangeEl = range;
      aux = h('div', { class: 'frow-range' }, h('span', { class: 'frow-end' }, String(min)), range, h('span', { class: 'frow-end' }, String(max)));
    } else if (d.placeholderSource) {
      aux = h('span', { class: 'frow-source' }, d.placeholderSource);
    }
    const value = d.type === 'bool'
      ? h('label', { class: 'frow-switch' }, this.input, h('span', { class: 'switch-track' }), this.switchText)
      : h('div', { class: 'frow-value' }, this.complex ? this.complex.el : this.input, this.viewEl);
    if (d.type === 'bool') (this.input as HTMLInputElement).classList.add('switch-input');
    this.input.addEventListener('change', () => this.paintSwitch());
    return h('div', { class: cls, dataset: { key: d.key } }, labelCol, h('div', { class: 'frow-aux' }, aux), value, h('div', { class: 'frow-unit' }, d.unit ? tk(d.unit) : ''), this.errorEl);
  }

  private rangeEl: HTMLInputElement | null = null;
  private boolMark = h('span', { class: 'bool-mark', hidden: true });
  private switchText = h('span', { class: 'switch-text' });

  private paintSwitch(): void {
    if (this.def.type === 'bool') this.switchText.textContent = (this.input as HTMLInputElement).checked ? t('core.ui.on') : t('core.ui.off');
  }

  /** Posuvník s krajními hodnotami vedle číselného pole; oba ovládají stejnou hodnotu (prázdné pole = posuvník na výchozí). */
  private makeSlider(num: HTMLInputElement): HTMLElement {
    const d = this.def;
    const min = d.min ?? 0;
    const max = d.maxValue ?? 100;
    const range = h('input', { type: 'range', class: 'field-range', min: String(min), max: String(max), step: Number.isInteger(min) && Number.isInteger(max) ? '1' : 'any', 'aria-label': tk(d.label) }) as HTMLInputElement;
    const sync = (): void => { const v = num.value === '' ? Number(d.default ?? min) : Number(num.value); range.value = String(Number.isNaN(v) ? min : v); };
    range.addEventListener('input', () => { num.value = range.value; this.onChange?.(); });
    num.addEventListener('input', sync);
    num.classList.add('field-num-short');
    this.syncSlider = sync;
    return h('div', { class: 'field-slider' }, num, d.unit ? h('span', { class: 'field-unit' }, tk(d.unit)) : null, h('span', { class: 'field-range-end' }, String(min)), range, h('span', { class: 'field-range-end' }, String(max)));
  }

  private syncSlider: (() => void) | null = null;

  private inputId(): string {
    return 'f-' + this.def.key + '-' + Math.random().toString(36).slice(2, 7);
  }

  private makeInput(): HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement {
    const d = this.def;
    switch (d.type) {
      case 'textarea':
        return h('textarea', { class: 'input', rows: String(d.rows || 4), maxlength: d.max ?? null });
      case 'select': {
        const sel = h('select', { class: 'input' });
        if (!d.required) sel.appendChild(h('option', { value: '' }, '—'));
        for (const o of d.options ?? []) sel.appendChild(h('option', { value: String(o.value) }, tk(o.label)));
        return sel;
      }
      case 'bool':
        return h('input', { type: 'checkbox' });
      case 'number':
        return h('input', { class: 'input', type: 'number', step: 'any', min: d.min ?? null, max: d.maxValue ?? null });
      case 'date':
        return h('input', { class: 'input', type: 'date' });
      case 'datetime':
        return h('input', { class: 'input', type: 'datetime-local' });
      case 'email':
        return h('input', { class: 'input', type: 'email', maxlength: d.max ?? null, autocomplete: 'off' });
      case 'password':
        return h('input', { class: 'input', type: 'password', maxlength: d.max ?? null, autocomplete: 'new-password' });
      default:
        return h('input', { class: 'input', type: 'text', maxlength: d.max ?? null, autocomplete: 'off' });
    }
  }

  setValue(v: unknown): void {
    const d = this.def;
    if (this.complex) {
      this.complex.set(v);
      this.el.classList.toggle('is-empty', v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0));
      return;
    }
    if (d.type === 'bool') (this.input as HTMLInputElement).checked = v === true || v === 1 || v === '1';
    else if (d.type === 'date') this.input.value = toInputDate(v);
    else if (d.type === 'datetime') this.input.value = toInputDateTime(v);
    else if (d.type === 'password') this.input.value = '';
    else this.input.value = v === null || v === undefined ? '' : String(v);
    this.syncSlider?.();
    this.paintSwitch();
    this.renderView(v);
  }

  value(): unknown {
    const d = this.def;
    if (this.complex) return this.complex.get();
    if (d.type === 'bool') return (this.input as HTMLInputElement).checked;
    const raw = this.input.value;
    if (raw === '') return d.type === 'select' && d.required ? (d.options?.[0]?.value ?? null) : null;
    if (d.type === 'number') return Number(raw);
    if (d.type === 'datetime') return new Date(raw).toISOString();
    return raw;
  }

  private renderView(v: unknown): void {
    const d = this.def;
    let text = '';
    if (d.type === 'select') text = tk(d.options?.find((o) => String(o.value) === String(v))?.label ?? '') || (v === null || v === undefined ? '' : String(v));
    else if (d.type === 'password') text = '••••••••';
    else if (d.type === 'date') text = fmtDate(v);
    else if (d.type === 'datetime') text = fmtDateTime(v);
    else if (d.type === 'number' && this.layout === 'rows' && v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v))) text = new Intl.NumberFormat('cs-CZ').format(Number(v));
    else text = v === null || v === undefined ? '' : String(v);
    const ph = !text && d.placeholder ? d.placeholder : ''; // prázdné pole se zděděnou hodnotou: ukázat šedě, co platí
    this.viewEl.textContent = (text && d.unit && this.layout !== 'rows' ? text + ' ' + tk(d.unit) : text) || ph || '—';
    this.viewEl.classList.toggle('placeholder', ph !== '');
    this.el.classList.toggle('is-empty', !text && !ph && this.def.type !== 'bool'); // ovládání bar: prázdná pole se v režimu čtení schovají
    this.viewEl.classList.toggle('multiline', d.type === 'textarea');
  }

  setEdit(edit: boolean): void {
    const editable = edit && !this.def.readonly;
    this.el.classList.toggle('edit', editable);
    this.el.classList.toggle('view', !editable);
    if (this.complex) {
      this.complex.setEdit(editable);
      return;
    }
    if (this.rangeEl) this.rangeEl.disabled = !editable;
    if (this.def.type === 'bool') {
      const cb = this.input as HTMLInputElement;
      cb.disabled = !editable;
      if (this.layout !== 'rows') { // čtení: místo šedého políčka značka (✓ / —)
        cb.hidden = !editable;
        this.boolMark.hidden = editable;
        this.boolMark.textContent = cb.checked ? '✓' : '—';
      }
    }
    else {
      this.control.hidden = !editable;
      this.input.hidden = !editable;
      this.viewEl.hidden = editable;
      if (!editable) this.renderView(this.value());
    }
  }

  setError(msg: string | null): void {
    this.errorEl.textContent = msg ? tk(msg) : '';
    this.el.classList.toggle('invalid', Boolean(msg));
    this.input.setAttribute('aria-invalid', msg ? 'true' : 'false');
  }

  setVisible(v: boolean): void {
    this.el.hidden = !v;
  }

  focus(): void {
    if (this.complex) this.complex.focus();
    else this.input.focus();
  }

  /** Kontrola na klientu (stejná pravidla jako Field::validate na serveru) – pro okamžitou odezvu; rozhoduje server. */
  check(): string | null {
    const d = this.def;
    const v = this.value();
    if (d.type === 'bool' || d.readonly) return null;
    if (v === null || v === '' || (Array.isArray(v) && !v.length)) return d.required ? 'core.val.required' : null;
    if (this.complex) return null;
    if ((d.type === 'text' || d.type === 'textarea') && d.max && String(v).length > d.max) return 'core.val.too_long';
    if (d.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v))) return 'core.val.email';
    if (d.type === 'number') {
      const n = Number(v);
      if (Number.isNaN(n)) return 'core.val.number';
      if ((d.min !== null && n < d.min) || (d.maxValue !== null && n > d.maxValue)) return d.min !== null && d.maxValue !== null ? t('core.val.range_mm', { min: d.min, max: d.maxValue }) : d.min !== null ? t('core.val.range_min', { min: d.min }) : t('core.val.range_max', { max: d.maxValue ?? 0 });
    }
    return null;
  }
}
